// 🧊 3D 부품 두 벌 합성 — 색 층 + 마스크(휘도 → 알파) · 마스크만 채우기 · 무채색 부품에 브랜드색 입히기.
//   쇼릴(`src/remotion/promo-reel/draw/primitives.ts` 에서 옮겨 옴 · 10-01 모션 브리핑 3D 층 2-1)과 모션 브리핑 무대(`Motion3dLayer`)가 같이 쓴다.
//   전부 순수(시간·난수 없음). ★필터 id·임시 캔버스는 종전 그대로★ — 쇼릴 그림 바이트 불변.

/** 두 벌 합성이 대상 캔버스에서 쓰는 것만 — 테스트가 캐스트 없이 가짜 컨텍스트를 줄 수 있다(타입만 좁힘 · 동작 불변). */
export type Matte2D = Pick<CanvasRenderingContext2D, "save" | "restore" | "drawImage" | "globalAlpha">;

let filterInstalled = false;
/** 마스크(휘도) → 알파 필터를 문서에 한 번 심는다(`destination-in` 은 알파만 보므로 R 을 A 로 옮긴다). */
function ensureLumToAlphaFilter(): boolean {
  if (typeof document === "undefined") return false;
  if (filterInstalled) return true;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", "0");
  svg.setAttribute("height", "0");
  svg.style.position = "absolute";
  svg.innerHTML = `<filter id="promo-reel-lum2alpha" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 0 0 0 0"/></filter>`;
  document.body.appendChild(svg);
  filterInstalled = true;
  return true;
}

let scratch: HTMLCanvasElement | null = null;
function scratchCanvas(w: number, h: number): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  if (!scratch) scratch = document.createElement("canvas");
  if (scratch.width !== w || scratch.height !== h) { scratch.width = w; scratch.height = h; }
  return scratch;
}

/**
 * 두 벌(색 + 마스크) → 한 장. 색 층을 임시 캔버스에 놓고 마스크를 `destination-in` 으로 곱한 뒤 `ctx` 에 얹는다.
 *   0단계 실측: 가장자리 1px 평균차 1.3~2.9/255 · 3px 밖 헤일로 0(알파 WebM 한 벌보다 정확).
 */
export function drawMatted(
  ctx: Matte2D, color: CanvasImageSource, matte: CanvasImageSource,
  x: number, y: number, w: number, h: number, alpha = 1,
): void {
  const tmp = scratchCanvas(Math.max(1, Math.round(w)), Math.max(1, Math.round(h)));
  if (!tmp) return;
  const t = tmp.getContext("2d");
  if (!t) return;
  t.save();
  t.globalCompositeOperation = "source-over";
  t.filter = "none";
  t.clearRect(0, 0, tmp.width, tmp.height);
  t.drawImage(color, 0, 0, tmp.width, tmp.height);
  t.globalCompositeOperation = "destination-in";
  if (ensureLumToAlphaFilter()) t.filter = "url(#promo-reel-lum2alpha)";
  t.drawImage(matte, 0, 0, tmp.width, tmp.height);
  t.restore();
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.drawImage(tmp, x, y, w, h);
  ctx.restore();
}

/** 마스크만 — 색 `fill` 을 마스크 모양으로 칠한다(조리개·계단 전환). `invert` 면 마스크 밖을 칠한다. */
export function drawMaskFill(
  ctx: Matte2D, matte: CanvasImageSource, fill: string, W: number, H: number, invert = false,
): void {
  const tmp = scratchCanvas(W, H);
  if (!tmp) return;
  const t = tmp.getContext("2d");
  if (!t) return;
  t.save();
  t.globalCompositeOperation = "source-over";
  t.filter = "none";
  t.clearRect(0, 0, W, H);
  t.fillStyle = fill;
  t.fillRect(0, 0, W, H);
  t.globalCompositeOperation = invert ? "destination-out" : "destination-in";
  if (ensureLumToAlphaFilter()) t.filter = "url(#promo-reel-lum2alpha)";
  t.drawImage(matte, 0, 0, W, H);
  t.restore();
  ctx.drawImage(tmp, 0, 0);
}

/**
 * 무채색 부품(`tint: "neutral"` · 16:9 모션 브리핑용)에 브랜드색을 입혀 얹는다 — 색 층 → 브랜드색 `color` 블렌드(색상·채도 = 브랜드 · 명도 = 층)
 *   → 마스크 `destination-in`. 명암(반광 금속의 하이라이트·그림자)은 남고 색만 바뀐다(굽기 세션 README «리모션이 알아야 할 것»).
 */
export function drawMattedTinted(
  ctx: Matte2D, color: CanvasImageSource, matte: CanvasImageSource, tint: string,
  x: number, y: number, w: number, h: number, alpha = 1,
): void {
  const tmp = scratchCanvas(Math.max(1, Math.round(w)), Math.max(1, Math.round(h)));
  if (!tmp) return;
  const t = tmp.getContext("2d");
  if (!t) return;
  t.save();
  t.globalCompositeOperation = "source-over";
  t.filter = "none";
  t.clearRect(0, 0, tmp.width, tmp.height);
  t.drawImage(color, 0, 0, tmp.width, tmp.height);
  t.globalCompositeOperation = "color";
  t.fillStyle = tint;
  t.fillRect(0, 0, tmp.width, tmp.height);
  t.globalCompositeOperation = "destination-in";
  if (ensureLumToAlphaFilter()) t.filter = "url(#promo-reel-lum2alpha)";
  t.drawImage(matte, 0, 0, tmp.width, tmp.height);
  t.restore();
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.drawImage(tmp, x, y, w, h);
  ctx.restore();
}
